/**
 * /accounting/bank-rec/:glAccountId — Per-account match workspace.
 *
 * Left: bank statement lines (unmatched first). Right: ranked match
 * suggestions from the GL clearing account, one-click Match / Ignore.
 * Includes CSV paste import for that account.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, X, Upload, RotateCcw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useBankFeed } from "@/data/accounting/bankFeed/BankFeedContext";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useAP } from "@/data/accounting/ap/APContext";
import { suggestBankTxnCategorization } from "@/data/accounting/bankFeed/categorizationSuggestions";
import { Sparkles } from "lucide-react";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export default function BankReconciliationPage() {
  const { glAccountId = "" } = useParams();
  const { getAccount, transactions, getSuggestions, importCsv, matchToJournal, ignore, resetMatch } = useBankFeed();
  const { accountById, journalEntries } = useGeneralLedger();
  const { bills } = useAP();
  const account = getAccount(glAccountId);
  const glAcc = accountById.get(glAccountId);

  // Derive a vendor catalog from posted bills so categorization can match descriptions.
  const vendors = useMemo(() => {
    const m = new Map<string, { id: string; name: string }>();
    bills.forEach(b => { if (!m.has(b.vendorId)) m.set(b.vendorId, { id: b.vendorId, name: b.vendorName }); });
    return [...m.values()];
  }, [bills]);

  const [search, setSearch] = useState("");
  const [csv, setCsv] = useState("");
  const [importing, setImporting] = useState(false);
  const [selectedTxnId, setSelectedTxnId] = useState<string | null>(null);

  const scoped = useMemo(
    () => transactions
      .filter(t => t.glAccountId === glAccountId)
      .filter(t => !search ||
        t.description.toLowerCase().includes(search.toLowerCase()) ||
        (t.reference ?? "").toLowerCase().includes(search.toLowerCase())),
    [transactions, glAccountId, search],
  );

  const unmatched = scoped.filter(t => t.status === "unmatched");
  const resolved = scoped.filter(t => t.status !== "unmatched");

  if (!account || !glAcc) {
    return (
      <CockpitPage>
      <BooksNavRail />
        <div className="p-8 text-center text-muted-foreground">
          Bank account not mapped.{" "}
          <Link to="/accounting/bank-rec" className="text-primary underline">Back to hub</Link>
        </div>
      </CockpitPage>
    );
  }

  const activeTxn = selectedTxnId
    ? scoped.find(t => t.id === selectedTxnId) ?? null
    : unmatched[0] ?? null;
  const suggestions = activeTxn ? getSuggestions(activeTxn.id) : [];

  const handleImport = () => {
    if (!csv.trim()) return;
    setImporting(true);
    try {
      const { imported, errors } = importCsv(csv, glAccountId);
      toast({
        title: `Imported ${imported} transaction${imported === 1 ? "" : "s"}`,
        description: errors.length ? `${errors.length} row(s) skipped.` : "Ready to reconcile.",
      });
      if (imported > 0) setCsv("");
    } finally {
      setImporting(false);
    }
  };

  const handleMatch = (txnId: string, jeId: string) => {
    try {
      matchToJournal(txnId, jeId);
      toast({ title: "Matched", description: "Reclassifying JE posted to GL." });
      setSelectedTxnId(null);
    } catch (e) {
      toast({ title: "Couldn't match", description: (e as Error).message, variant: "destructive" });
    }
  };

  return (
    <CockpitPage>
      <div className="mb-6 flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/accounting/bank-rec"><ArrowLeft className="h-4 w-4 mr-1" /> Hub</Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{account.label}</h1>
          <p className="text-xs text-muted-foreground">
            GL {glAcc.number} · {glAcc.name} · clearing account {account.clearingAccountId}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{unmatched.length} unmatched</Badge>
          <Badge variant="secondary">{resolved.length} resolved</Badge>
        </div>
      </div>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Import statement (CSV)</span>
            <span className="text-xs text-muted-foreground">date, description, amount[, reference]</span>
          </div>
          <Textarea
            value={csv}
            onChange={e => setCsv(e.target.value)}
            placeholder={`2026-06-20, Customer payment - Smith, 4250.00, DEP1042\n2026-06-21, Amex autopay, -8420.55, ACH`}
            className="font-mono text-xs h-24"
          />
          <div className="mt-2 flex justify-end">
            <Button size="sm" disabled={!csv.trim() || importing} onClick={handleImport}>
              {importing ? "Importing…" : "Import"}
            </Button>
          </div>
        </div>
      </CockpitSection>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-4">
        {/* Statement lines */}
        <div className="lg:col-span-3">
          <div className="rounded-md border border-border bg-card">
            <div className="flex items-center justify-between p-3 border-b border-border">
              <span className="text-sm font-medium">Statement lines</span>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search description…"
                  className="h-7 pl-7 text-xs w-48"
                />
              </div>
            </div>
            <div className="divide-y divide-border max-h-[520px] overflow-auto">
              {scoped.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No transactions yet — paste a CSV above to start.
                </div>
              )}
              {scoped.map(t => {
                const isActive = activeTxn?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTxnId(t.id)}
                    className={`w-full text-left p-3 hover:bg-accent/40 transition-colors ${isActive ? "bg-accent/60" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{t.description}</div>
                        <div className="text-xs text-muted-foreground">
                          {fmtDate(t.postedDate)} {t.reference ? `· ${t.reference}` : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-sm font-semibold tabular-nums ${t.side === "inflow" ? "text-foreground" : "text-muted-foreground"}`}>
                          {t.side === "inflow" ? "+" : "−"}{fmt(t.amount)}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide">
                          {t.status === "matched" && <Badge variant="secondary" className="h-4 px-1 text-[10px]">Matched</Badge>}
                          {t.status === "ignored" && <Badge variant="outline" className="h-4 px-1 text-[10px]">Ignored</Badge>}
                          {t.status === "unmatched" && <Badge variant="destructive" className="h-4 px-1 text-[10px]">Open</Badge>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Match panel */}
        <div className="lg:col-span-2">
          <div className="rounded-md border border-border bg-card p-4 sticky top-4">
            {!activeTxn ? (
              <div className="text-sm text-muted-foreground">Select a transaction to match.</div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Selected</div>
                  <div className="text-sm font-medium mt-1">{activeTxn.description}</div>
                  <div className="text-xs text-muted-foreground">
                    {fmtDate(activeTxn.postedDate)} · {activeTxn.side === "inflow" ? "Inflow" : "Outflow"} {fmt(activeTxn.amount)}
                  </div>
                </div>

                {activeTxn.status === "matched" && (
                  <div className="rounded-sm border border-border p-2 text-xs">
                    <div className="font-medium">Matched</div>
                    <div className="text-muted-foreground">
                      JE {journalEntries.find(j => j.id === activeTxn.matchedJournalEntryId)?.number ?? "—"}{" "}
                      by {activeTxn.matchedBy ?? "—"}
                    </div>
                    <Button size="sm" variant="ghost" className="mt-2 h-7"
                      onClick={() => resetMatch(activeTxn.id)}>
                      <RotateCcw className="h-3 w-3 mr-1" /> Reopen
                    </Button>
                  </div>
                )}

                {activeTxn.status === "ignored" && (
                  <div className="rounded-sm border border-border p-2 text-xs">
                    <div className="font-medium">Ignored</div>
                    <Button size="sm" variant="ghost" className="mt-2 h-7"
                      onClick={() => resetMatch(activeTxn.id)}>
                      <RotateCcw className="h-3 w-3 mr-1" /> Reopen
                    </Button>
                  </div>
                )}

                {activeTxn.status === "unmatched" && (
                  <>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                      Suggested matches ({suggestions.length})
                    </div>
                    <div className="space-y-2">
                      {suggestions.length === 0 && (() => {
                        const cat = suggestBankTxnCategorization(activeTxn, vendors, journalEntries);
                        const catAcct = cat ? accountById.get(cat.accountId) : null;
                        return (
                          <div className="space-y-2">
                            <div className="text-xs text-muted-foreground">
                              No candidates on {accountById.get(account.clearingAccountId)?.name ?? "clearing account"}. Either the operational event isn't posted yet, or this is a new line.
                            </div>
                            {cat && catAcct && cat.confidence >= 0.4 && (
                              <div className="rounded-sm border border-primary/30 bg-primary/5 p-2">
                                <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                                  <Sparkles className="h-3 w-3" /> Suggested categorization
                                </div>
                                <div className="mt-1 text-sm font-medium">
                                  {catAcct.number} · {catAcct.name}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                  {cat.reason} <span className="opacity-60">· {(cat.confidence * 100).toFixed(0)}% confidence</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {suggestions.map(s => {
                        const je = journalEntries.find(j => j.id === s.journalEntryId);
                        if (!je) return null;
                        return (
                          <div key={s.journalEntryId} className="rounded-sm border border-border p-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{je.number}</div>
                                <div className="text-xs text-muted-foreground truncate">{je.description}</div>
                                <div className="text-[11px] text-muted-foreground mt-1">
                                  {s.reasons.join(" · ")}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-xs font-semibold tabular-nums">{s.score}</div>
                                <Button size="sm" className="mt-1 h-7"
                                  onClick={() => handleMatch(activeTxn.id, s.journalEntryId)}>
                                  <Check className="h-3 w-3 mr-1" /> Match
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 pt-3 border-t border-border">
                      <Button size="sm" variant="ghost" className="h-7"
                        onClick={() => { ignore(activeTxn.id); setSelectedTxnId(null); }}>
                        <X className="h-3 w-3 mr-1" /> Ignore this line
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </CockpitPage>
  );
}
